import { useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { ExportButton } from "../../components/ExportButton";
import { Registar, type Kolona } from "../../components/Registar";
import { Select } from "../../components/Select";
import { FulfilmentBar } from "../../components/FulfilmentBar";
import { formatDate, formatMoneyCode, formatNumber } from "../../lib/format";
import {
  procurementContractsApi,
  type ProcurementContract,
  type ProcurementContractStatus,
} from "../../api/procurementContracts";

const STATUSES: ProcurementContractStatus[] = ["DRAFT", "ACTIVE", "FULFILLED", "CANCELLED"];

/**
 * Supplier-facing "ugovori o otkupu" list. Uses the same
 * /api/procurement/contracts the Otkup admin controller does, but RLS +
 * service-layer scoping mean a SUPP_MANAGER caller only ever sees their own
 * company's rows.
 *
 * <p>The register reads one page from the server. It has to: the intake records bring in 580
 * concluded contracts, and a row carries both the agreed terms and the computed fulfilment.
 */
export default function ProcurementContractsPage() {
  const { t, i18n } = useTranslation();
  const jezik = i18n.resolvedLanguage;
  const [statusFilter, setStatusFilter] = useState<ProcurementContractStatus | "">("");

  // The exports go through the same filter as the table, built from the same value the page is read
  // with, so what is on the screen is what is downloaded.
  const izvozUpit = statusFilter ? `?status=${statusFilter}` : "";

  const kolone = useMemo<Kolona<ProcurementContract>[]>(
    () => [
      {
        kljuc: "reference",
        zaglavlje: t("procurement.contracts.list.col.reference"),
        labela: t("procurement.contracts.list.col.reference"),
        crtaj: (c) => <span style={mono}>{c.referenceNumber}</span>,
      },
      {
        kljuc: "culture",
        zaglavlje: t("procurement.contracts.list.col.culture"),
        labela: t("procurement.contracts.list.col.culture"),
        crtaj: (c) => c.culture ?? "—",
      },
      {
        kljuc: "quantity",
        zaglavlje: t("procurement.contracts.list.col.quantity"),
        labela: t("procurement.contracts.list.col.quantity"),
        broj: true,
        crtaj: (c) => `${formatNumber(c.quantityT, jezik)} t`,
      },
      {
        kljuc: "price",
        zaglavlje: t("procurement.contracts.list.col.price"),
        labela: t("procurement.contracts.list.col.price"),
        broj: true,
        // Valuta se ispisuje kad je dogovorena, i ne pogađa se kad nije: na klijentovom registru cena
        // u evrima po toni stoji na 71 % ugovora. Pretpostavljena valuta pored broja čita se kao
        // dogovorena, pa se bez nje piše samo broj.
        crtaj: (c) =>
          c.pricePerT == null
            ? "—"
            : c.priceCurrency
              ? formatMoneyCode(c.pricePerT, c.priceCurrency, jezik)
              : formatNumber(c.pricePerT, jezik),
      },
      {
        kljuc: "harvestYear",
        zaglavlje: t("procurement.contracts.list.col.harvestYear"),
        labela: t("procurement.contracts.list.col.harvestYear"),
        // Godina je oznaka, ne količina: ide bez razdvajanja hiljada i bez ređanja po desno.
        crtaj: (c) => String(c.harvestYear),
      },
      {
        kljuc: "fulfilment",
        zaglavlje: t("procurement.contract.fulfilment", "Fulfilment"),
        labela: t("procurement.contract.fulfilment", "Fulfilment"),
        crtaj: (c) => (
          <FulfilmentBar
            settledT={c.settledT}
            inProgressT={c.inProgressT}
            remainingT={c.remainingT}
            fulfilmentPct={c.fulfilmentPct}
          />
        ),
      },
      {
        kljuc: "status",
        zaglavlje: t("procurement.contracts.list.col.status"),
        labela: t("procurement.contracts.list.col.status"),
        crtaj: (c) => <StatusBadge status={c.status} />,
      },
      {
        kljuc: "effective",
        zaglavlje: t("procurement.contracts.list.col.effective"),
        labela: t("procurement.contracts.list.col.effective"),
        crtaj: (c) => (
          <span style={prigusen}>{c.effectiveDate ? formatDate(c.effectiveDate, jezik) : "—"}</span>
        ),
      },
      {
        kljuc: "expires",
        zaglavlje: t("procurement.contracts.list.col.expires"),
        labela: t("procurement.contracts.list.col.expires"),
        crtaj: (c) => (
          <span style={prigusen}>{c.expiresAt ? formatDate(c.expiresAt, jezik) : "—"}</span>
        ),
      },
    ],
    [t, jezik],
  );

  return (
    <Registar<ProcurementContract>
      naslov={t("procurement.contracts.list.title")}
      filteri={
        <>
          <Select<string>
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as ProcurementContractStatus | "")}
            ariaLabel={t("procurement.contracts.list.allStatuses")}
            style={{ minWidth: 190 }}
            options={[
              { value: "", label: t("procurement.contracts.list.allStatuses") },
              ...STATUSES.map((s) => ({
                value: s,
                label: t(`procurement.contracts.status.${s.toLowerCase()}`),
              })),
            ]}
          />
          {/* Registar nosi jedan izvoz, a ovaj spisak se preuzima u dva oblika, pa oba dugmeta stoje
              ovde — sa istim filterom koji nosi i tabela. */}
          <ExportButton
            path={`/exports/procurement-contracts.csv${izvozUpit}`}
            label={t("procurement.contracts.list.exportCsv", "CSV")}
            filename="procurement-contracts.csv"
          />
          <ExportButton
            path={`/exports/procurement-contracts.xlsx${izvozUpit}`}
            label={t("procurement.contracts.list.exportXlsx", "XLSX")}
            filename="procurement-contracts.xlsx"
          />
        </>
      }
      kljucFiltera={statusFilter}
      filtrirano={statusFilter !== ""}
      ucitaj={(strana, velicina) =>
        procurementContractsApi.list({ status: statusFilter || undefined }, strana, velicina)
      }
      kolone={kolone}
      kljucReda={(c) => c.id}
      vezaNaRed={(c) => `/procurement/contracts/${c.id}`}
      prazno={t("procurement.contracts.list.empty")}
      praznoUzFilter={t(
        "procurement.contracts.list.emptyFiltered",
        "Nijedan ugovor nije u izabranom statusu.",
      )}
    />
  );
}

export function StatusBadge({ status }: { status: ProcurementContractStatus }) {
  const { t } = useTranslation();
  const palette: Record<ProcurementContractStatus, { bg: string; fg: string }> = {
    DRAFT:     { bg: "var(--surface-muted)", fg: "var(--ink-2)" },
    ACTIVE:    { bg: "var(--ok-bg)", fg: "var(--ok)" },
    FULFILLED: { bg: "var(--brand-primary-50)", fg: "var(--brand-primary)" },
    CANCELLED: { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  // A state the browser does not know is a state the server added since this bundle was built,
  // which is exactly what a reader with an open tab sees during a release. An unknown state is
  // shown plainly rather than taking the screen down with it.
  const c = palette[status] ?? { bg: "var(--surface-2)", fg: "var(--ink-2)" };
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
      {t(`procurement.contracts.status.${status.toLowerCase()}`)}
    </span>
  );
}

const mono: CSSProperties = { fontFamily: "var(--mono)", fontSize: 12 };
const prigusen: CSSProperties = { color: "var(--ink-2)" };
